import { useEffect, useRef, useState } from "react";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import type { ScoreRow, ToastTone } from "../components/contracts";
import { Footer } from "../components/Footer";
import { HostAbsentNotice } from "../components/HostAbsentNotice";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx, stagger, errorText } from "../components/util";

const COUNT_MS = 700;

export interface ScoreboardProps {
  playerId: string;
  round: number;
  roundCount: number;
  rows: ScoreRow[];
  /** Host, OR anyone once the host has gone quiet. */
  canControl: boolean;
  /** True only in the widened case, so the extra button gets explained. */
  hostAbsent: boolean;
  /** `rounds.advancePhase` — next round, or the podium. */
  onAdvance: () => Promise<void>;
  onToast: (text: string, tone?: ToastTone) => void;
}

export function Scoreboard({
  playerId,
  round,
  roundCount,
  rows,
  canControl,
  hostAbsent,
  onAdvance,
  onToast,
}: ScoreboardProps) {
  const [advancing, setAdvancing] = useState(false);
  const isFinalRound = round >= roundCount;

  async function goNext() {
    setAdvancing(true);
    try {
      await onAdvance();
    } catch (err) {
      setAdvancing(false);
      onToast(errorText(err, "Couldn't start the next round."), "error");
    }
  }

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>
              Round {round} of {roundCount}
            </span>
            <span aria-hidden="true">&middot;</span>
            <span>Scores</span>
          </>
        }
      />

      <main className="shell bd-stack-lg">
        <Panel title="This round" aside={<Badge tone="carbon">Round {round}</Badge>}>
          <div className="bd-stagger">
            {rows.map((row, i) => (
              <div
                key={row.playerId}
                className={cx("row", row.playerId === playerId && "row-highlight")}
                style={stagger(i)}
              >
                <span className="bd-score-rank" aria-hidden="true">
                  {row.rank}
                </span>
                <span className="t-link grow truncate">
                  {row.name}
                  {row.playerId === playerId ? (
                    <span className="t-micro muted"> (you)</span>
                  ) : null}
                </span>
                <span
                  className={cx("t-label", "bd-score-delta", "bd-delta-in", row.delta > 0 && "bd-score-up")}
                  style={stagger(i)}
                >
                  {row.delta > 0 ? `+${row.delta}` : row.delta}
                </span>
                <CountUp className="bd-score-total" from={row.total - row.delta} to={row.total} />
                <span className="bd-sr-only">
                  {row.name}: {row.delta > 0 ? `plus ${row.delta}` : "no points"} this round,{" "}
                  {row.total} total, rank {row.rank}.
                </span>
              </div>
            ))}
          </div>
        </Panel>

        <div className="plate chamfer bd-pad-lg center stack">
          {hostAbsent ? <HostAbsentNotice /> : null}
          {canControl ? (
            <Button
              variant="submit"
              block
              beacon
              disabled={advancing}
              onClick={() => void goNext()}
            >
              {advancing ? "Dealing…" : isFinalRound ? "See the podium" : `Start round ${round + 1}`}
            </Button>
          ) : (
            <p className="t-body muted" role="status">
              {isFinalRound
                ? "Waiting for the host to reveal the podium."
                : "Waiting for the host to start the next round."}
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

/** Score tick-up. Lands on `to` even when reduced motion collapses the run. */
function CountUp({ from, to, className }: { from: number; to: number; className?: string }) {
  const [value, setValue] = useState(from);
  const raf = useRef(0);

  useEffect(() => {
    if (from === to) {
      setValue(to);
      return;
    }
    const startedAt = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - startedAt) / COUNT_MS);
      setValue(Math.round(from + (to - from) * t));
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [from, to]);

  return (
    <span className={cx(className, "bd-tick")} key={to} aria-hidden="true">
      {value}
    </span>
  );
}
